/**
 * Layout Component - Main app wrapper with sidebar and navigation
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, CreditCard, Sparkles, Tv, Activity, Users, 
  History, Wallet, User, Shield, LogOut, Menu, Bookmark, Phone, MessageCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NotificationsDropdown from './NotificationsDropdown';
import ThemeToggle from './ThemeToggle';
import ContactSupportButtons, { supportTelHref, supportWhatsAppHref } from './ContactSupportButtons';
import appIcon from '../assets/brand/app-icon.png';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: "/", icon: Home, label: "Home" },
    { path: "/wallet", icon: Wallet, label: "Wallet" },
    { path: "/buy", icon: CreditCard, label: "Pay Bill" },
    { path: "/smart", icon: Sparkles, label: "Smart" },
    { path: "/subscriptions", icon: Tv, label: "Subscriptions" },
    { path: "/monitoring", icon: Activity, label: "Monitoring" },
    { path: "/organization", icon: Users, label: "Family & teams" },
    { path: "/history", icon: History, label: "History" },
    { path: "/accounts", icon: Bookmark, label: "Saved Meters" },
    { path: "/profile", icon: User, label: "Profile" },
  ];

  if (user?.is_admin) {
    navItems.push({ path: "/admin", icon: Shield, label: "Admin" });
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border/50 flex-col overflow-hidden">
        <div className="p-6 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-3">
            <img src={appIcon} alt="SmartPay Utilities" className="w-10 h-10 rounded-xl shrink-0" />
            <div>
              <h1 className="font-bold text-foreground">SmartPay</h1>
              <p className="text-xs text-muted-foreground">Your home bills</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
          {navItems.map((item) => (
            <button key={item.path} onClick={() => navigate(item.path)}
              className={`nav-link w-full ${location.pathname === item.path ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}>
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-border/50 space-y-4 shrink-0">
          <ContactSupportButtons compact />
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center shrink-0">
              {user?.is_admin ? <Shield className="w-5 h-5 text-primary" /> : <User className="w-5 h-5 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name || "User"}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.phone}</p>
            </div>
          </div>
          <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Desktop top bar — notifications top-right */}
      <header className="hidden md:flex fixed top-0 left-64 right-0 h-14 bg-background/95 backdrop-blur-lg border-b border-border/50 z-30 px-6 items-center justify-end gap-1">
        <ThemeToggle />
        <NotificationsDropdown />
      </header>

      {/* Mobile Header */}
      <header
        className="md:hidden fixed top-0 left-0 right-0 h-[calc(4rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-card/95 backdrop-blur-lg border-b border-border/50 z-40 px-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-3 min-w-0">
          <img src={appIcon} alt="SmartPay Utilities" className="w-8 h-8 rounded-lg shrink-0" />
          <h1 className="font-bold truncate">SmartPay Utilities</h1>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <ThemeToggle />
          <NotificationsDropdown />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 hover:bg-muted rounded-lg" aria-label="Open menu">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 bg-black/60 z-50" onClick={() => setMobileMenuOpen(false)}>
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 25 }}
              className="absolute right-0 top-0 bottom-0 w-72 max-w-[85vw] bg-card border-l border-border/50 flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 pt-[calc(1rem+env(safe-area-inset-top))] border-b border-border/50 shrink-0">
                <p className="font-medium truncate">{user?.full_name || "User"}</p>
                <p className="text-sm text-muted-foreground truncate">{user?.phone}</p>
                {user?.is_admin && <span className="inline-block mt-1 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">Admin</span>}
              </div>
              {/* Scrollable so a long nav list never gets covered by the
                  Logout footer below — Logout sits in normal flow, not
                  absolutely positioned over the last nav items. */}
              <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                {navItems.map((item) => (
                  <button key={item.path} onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                    className={`nav-link w-full ${location.pathname === item.path ? "active" : ""}`}>
                    <item.icon className="w-5 h-5" /> {item.label}
                  </button>
                ))}
              </nav>
              <div className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-border/50 shrink-0 space-y-4">
                <ContactSupportButtons compact />
                <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-500/10 rounded-lg">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden mobile-nav pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around py-2">
          {navItems.slice(0, 5).map((item) => (
            <button key={item.path} onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all min-w-0 ${location.pathname === item.path ? "text-primary" : "text-muted-foreground"}`}>
              <item.icon className="w-5 h-5" />
              <span className="text-xs truncate">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="md:ml-64 pt-[calc(4rem+env(safe-area-inset-top))] md:pt-14 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0 min-h-screen">
        <div className="p-4 md:p-8">{children}</div>
      </main>

      {/* Mobile floating support — WhatsApp + call */}
      <div className="md:hidden fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-30 flex flex-col gap-2">
        <a
          href={supportTelHref}
          className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center"
          aria-label="Call support"
          data-testid="support-call-fab"
        >
          <Phone className="w-5 h-5" />
        </a>
        <a
          href={supportWhatsAppHref}
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-emerald-600 text-white shadow-lg flex items-center justify-center"
          aria-label="WhatsApp support"
          data-testid="support-whatsapp-fab"
        >
          <MessageCircle className="w-5 h-5" />
        </a>
      </div>
    </div>
  );
};

export default Layout;